/** The mutation vocabulary every adventure rule writes through.
 *
 * D1 splits mutability in two: the battle kernel journals in place, the
 * adventure kernel clones the whole state and mutates the clone. So there is no
 * undo machinery here - `executeCommand` clones once, hands the clone to these
 * helpers, and throws the clone away if the command turns out to be illegal.
 *
 * Nothing in this file decides anything; it applies.
 */

import type { GameEvent } from "../events.js";
import type { Modifier } from "../modifiers.js";
import type {
  ArmyId, HeroId, IdKind, InteractionId, ObjectId, PlayerId, TownId,
} from "../ids.js";
import { asArmyId, asHeroId, asObjectId, asPlayerId, asTownId } from "../ids.js";
import type { ContentRegistry } from "../content/registry.js";
import type { RngStream } from "../rng/streams.js";
import { nextBounded } from "../rng/pcg32.js";
import type {
  ArmyState, FogState, GameState, HeroState, InteractionPrompt, InteractionResponse,
  MapObjectState, PendingInteraction, PlayerState, ResourceKind, Resources,
  ResumeToken, TownState, TurnState,
} from "../state.js";
import { RESOURCES, ZERO_RESOURCES } from "../state.js";

/** Shallow-writable view of a plain-data shape. The clone is ours to write. */
export type Mut<T> = { -readonly [K in keyof T]: T[K] };

/**
 * Answers derived from the whole map that a rule asks for far more often than
 * the map changes.
 *
 * `kingdomModifiers` is the case this exists for: `statFor` reaches it, so it is
 * read on every movement-legality query - tens of thousands of times per AI turn
 * - and answering it costs a scan of every town and every map object. What it
 * depends on is narrow: which towns and which flagged objects a player owns.
 *
 * The memo is not dated and holds no snapshot. It is emptied outright by the
 * writers below, so a town that changes hands in the middle of a command is
 * already forgotten by the time the next hero asks. A `Ctx` also lives for
 * exactly one command (`executeCommand` clones the state and builds a fresh one)
 * and holds no reference to any state it did not get handed, so nothing here can
 * outlive the state it was computed from.
 */
export interface DerivedCache {
  /** `kingdomModifiers` by owner. */
  readonly kingdom: Map<PlayerId, readonly Modifier[]>;
}

export interface Ctx {
  readonly s: Mut<GameState>;
  readonly content: ContentRegistry;
  readonly events: GameEvent[];
  readonly derived: DerivedCache;
}

export function makeCtx(state: GameState, content: ContentRegistry): Ctx {
  return {
    s: state as Mut<GameState>, content, events: [], derived: { kingdom: new Map() },
  };
}

/**
 * Forget every derived answer. Called by each writer that can move a town or an
 * object, which is deliberately blunter than the dependencies need: a `Ctx` is
 * cheap to refill and a missed invalidation is a desync, so the write paths do
 * not try to work out whether the field they touched was one anybody read.
 */
function invalidateDerived(c: Ctx): void {
  c.derived.kingdom.clear();
}

// ---------------------------------------------------------------------- rng

/** Draw from a named stream and write the advanced stream back into state (I5). */
export function rollBounded(c: Ctx, stream: RngStream, bound: number): number {
  if (bound <= 1) return 0;
  const cur = c.s.rng[stream]!;
  const [nxt, v] = nextBounded(cur, bound);
  (c.s.rng as Mut<Record<RngStream, typeof cur>>)[stream] = nxt;
  return v;
}

export const rollRange = (c: Ctx, stream: RngStream, lo: number, hi: number): number =>
  hi <= lo ? lo : lo + rollBounded(c, stream, hi - lo + 1);

export const rollChanceBp = (c: Ctx, stream: RngStream, bp: number): boolean =>
  bp <= 0 ? false : bp >= 10000 ? true : rollBounded(c, stream, 10000) < bp;

/**
 * Pick one of `n` weighted options in basis points. The table is walked in the
 * given order and the roll is a single draw, so adding an option to the end of a
 * table never reshuffles the ones before it.
 */
export function rollWeighted(c: Ctx, stream: RngStream, weights: readonly number[]): number {
  let total = 0;
  for (const w of weights) total += w > 0 ? w : 0;
  if (total <= 0) return 0;
  let r = rollBounded(c, stream, total);
  for (let i = 0; i < weights.length; i++) {
    const w = weights[i]! > 0 ? weights[i]! : 0;
    if (r < w) return i;
    r -= w;
  }
  return weights.length - 1;
}

// ----------------------------------------------------------------------- ids

/** Runtime ids are minted from counters in state, never from randomness (3.1). */
export function mintId(c: Ctx, kind: IdKind): string {
  const n = (c.s.counters[kind] ?? 0) + 1;
  (c.s.counters as Mut<Record<IdKind, number>>)[kind] = n;
  return `${kind}:${n}`;
}

export const mintHeroId = (c: Ctx): HeroId => asHeroId(mintId(c, "hero"));
export const mintArmyId = (c: Ctx): ArmyId => asArmyId(mintId(c, "army"));
export const mintTownId = (c: Ctx): TownId => asTownId(mintId(c, "town"));
export const mintObjectId = (c: Ctx): ObjectId => asObjectId(mintId(c, "object"));
export const mintInteractionId = (c: Ctx): InteractionId =>
  mintId(c, "interaction") as InteractionId;

// ------------------------------------------------------------------ entities

export const hero = (c: Ctx, id: HeroId): HeroState | null => c.s.heroes[id] ?? null;
export const town = (c: Ctx, id: TownId): TownState | null => c.s.towns[id] ?? null;
export const army = (c: Ctx, id: ArmyId): ArmyState | null => c.s.armies[id] ?? null;
export const player = (c: Ctx, id: PlayerId): PlayerState | null => c.s.players[id] ?? null;
export const mapObject = (c: Ctx, id: ObjectId): MapObjectState | null => c.s.objects[id] ?? null;

type Rec<K extends string, V> = Record<K, V>;

function patch<K extends string, V extends object>(
  map: Readonly<Rec<K, V>>, id: K, p: Partial<Mut<V>>,
): void {
  const cur = (map as Rec<K, V>)[id];
  if (cur === undefined) return;
  (map as Rec<K, V>)[id] = { ...cur, ...p };
}

export const patchHero = (c: Ctx, id: HeroId, p: Partial<Mut<HeroState>>): void =>
  patch(c.s.heroes, id, p);
export const patchTown = (c: Ctx, id: TownId, p: Partial<Mut<TownState>>): void => {
  invalidateDerived(c);
  patch(c.s.towns, id, p);
};
export const patchArmy = (c: Ctx, id: ArmyId, p: Partial<Mut<ArmyState>>): void =>
  patch(c.s.armies, id, p);
export const patchPlayer = (c: Ctx, id: PlayerId, p: Partial<Mut<PlayerState>>): void =>
  patch(c.s.players, id, p);
export const patchObject = (c: Ctx, id: ObjectId, p: Partial<Mut<MapObjectState>>): void => {
  invalidateDerived(c);
  patch(c.s.objects, id, p);
};
export const patchTurn = (c: Ctx, p: Partial<Mut<TurnState>>): void => {
  c.s.turn = { ...c.s.turn, ...p };
};

export function putHero(c: Ctx, h: HeroState): void {
  (c.s.heroes as Rec<HeroId, HeroState>)[h.id] = h;
}
export function putTown(c: Ctx, t: TownState): void {
  invalidateDerived(c);
  (c.s.towns as Rec<TownId, TownState>)[t.id] = t;
}
export function putArmy(c: Ctx, a: ArmyState): void {
  (c.s.armies as Rec<ArmyId, ArmyState>)[a.id] = a;
}
export function putObject(c: Ctx, o: MapObjectState): void {
  invalidateDerived(c);
  (c.s.objects as Rec<ObjectId, MapObjectState>)[o.id] = o;
}
export function putPlayer(c: Ctx, p: PlayerState): void {
  (c.s.players as Rec<PlayerId, PlayerState>)[p.id] = p;
}
export function dropHero(c: Ctx, id: HeroId): void {
  delete (c.s.heroes as Rec<HeroId, HeroState>)[id];
}
export function dropArmy(c: Ctx, id: ArmyId): void {
  delete (c.s.armies as Rec<ArmyId, ArmyState>)[id];
}
export function putFog(c: Ctx, id: PlayerId, f: FogState): void {
  (c.s.fog as Rec<PlayerId, FogState>)[id] = f;
}
export const fogOf = (c: Ctx, id: PlayerId): FogState | null => c.s.fog[id] ?? null;

// --------------------------------------------------------------- resources

export function addResource(c: Ctx, p: PlayerId, r: ResourceKind, amount: number): void {
  if (amount === 0) return;
  const pl = c.s.players[p];
  if (pl === undefined) return;
  const have = pl.resources[r];
  const next = have + amount;
  patchPlayer(c, p, { resources: { ...pl.resources, [r]: next < 0 ? 0 : next } });
  c.events.push(amount > 0
    ? { type: "ResourceGained", player: p, resource: r, amount }
    : { type: "ResourceSpent", player: p, resource: r, amount: -amount });
}

export function addResources(c: Ctx, p: PlayerId, delta: Partial<Resources>): void {
  for (const r of RESOURCES) {
    const v = delta[r];
    if (v !== undefined && v !== 0) addResource(c, p, r, v);
  }
}

/** The first resource the player cannot afford, or null. Order is RESOURCES order. */
export function missingResource(
  have: Resources, cost: Partial<Resources>,
): { readonly resource: ResourceKind; readonly have: number; readonly need: number } | null {
  for (const r of RESOURCES) {
    const need = cost[r] ?? 0;
    if (need > have[r]) return { resource: r, have: have[r], need };
  }
  return null;
}

export function scaleCost(cost: Partial<Resources>, n: number): Partial<Resources> {
  const out: Mut<Partial<Resources>> = {};
  for (const r of RESOURCES) {
    const v = cost[r];
    if (v !== undefined && v !== 0) out[r] = v * n;
  }
  return out;
}

export function negate(cost: Partial<Resources>): Partial<Resources> {
  const out: Mut<Partial<Resources>> = {};
  for (const r of RESOURCES) {
    const v = cost[r];
    if (v !== undefined && v !== 0) out[r] = -v;
  }
  return out;
}

export const fullResources = (partial: Partial<Resources>): Resources => {
  const out: Mut<Resources> = { ...ZERO_RESOURCES };
  for (const r of RESOURCES) out[r] = partial[r] ?? 0;
  return out;
};

// ------------------------------------------------------------- interactions

/**
 * Raise a question and stop. Exactly one interaction may be outstanding: the
 * slot in `GameState` is single, and chaining is done by the resume token, not
 * by a queue - a treasure chest that levels a hero raises its level-up prompt
 * only once the chest's own prompt has been answered.
 *
 * Returns false when a prompt is already open, which is the caller's signal to
 * resolve its choice itself rather than ask.
 */
export function raise(
  c: Ctx, playerId: PlayerId, prompt: InteractionPrompt,
  legalResponses: readonly InteractionResponse[], resumeToken: ResumeToken,
): boolean {
  if (c.s.pending !== null) return false;
  const pending: PendingInteraction = {
    id: mintInteractionId(c), playerId, prompt, legalResponses, resumeToken,
  };
  c.s.pending = pending;
  c.events.push({ type: "InteractionOpened", player: playerId, prompt });
  return true;
}

export function closePending(c: Ctx): void {
  const p = c.s.pending;
  if (p === null) return;
  c.s.pending = null;
  c.events.push({ type: "InteractionClosed", player: p.playerId });
}

export const OK_CANCEL: readonly InteractionResponse[] =
  Object.freeze([{ kind: "OK" as const }, { kind: "CANCEL" as const }]);
export const OK_ONLY: readonly InteractionResponse[] = Object.freeze([{ kind: "OK" as const }]);

export const optionResponses = (n: number): readonly InteractionResponse[] => {
  const out: InteractionResponse[] = [];
  for (let i = 0; i < n; i++) out.push({ kind: "OPTION", index: i });
  return out;
};

// --------------------------------------------------------------- token data

export type TokenData = ResumeToken["data"];

export const tokenNum = (t: ResumeToken, k: string, fallback = 0): number => {
  const v = t.data[k];
  return typeof v === "number" ? v : fallback;
};
export const tokenStr = (t: ResumeToken, k: string, fallback = ""): string => {
  const v = t.data[k];
  return typeof v === "string" ? v : fallback;
};
export const tokenPath = (t: ResumeToken, k = "path"): readonly number[] => {
  const v = t.data[k];
  return Array.isArray(v) ? (v as readonly number[]) : [];
};

// ------------------------------------------------------------------ players

export const asPlayer = (s: string): PlayerId => asPlayerId(s);

// -------------------------------------------------------------------- clock

/**
 * Days since the first, from the calendar `TurnState` actually stores. Lives
 * here rather than in `turn.ts` so that the fog can age a snapshot without the
 * turn machine and the visibility pass importing each other.
 */
export const DAYS_PER_WEEK = 7;
export const WEEKS_PER_MONTH = 4;

export const absoluteDay = (s: { readonly turn: TurnState }): number =>
  (s.turn.month - 1) * DAYS_PER_WEEK * WEEKS_PER_MONTH
  + (s.turn.week - 1) * DAYS_PER_WEEK + s.turn.day;
